import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import { Box, TextField } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExperimentConfig, ExperimentDeviceRequestActionEnum } from '../../../backendsdk';
import {
    TrackedButton as Button,
    TrackedDialog as Dialog,
    TrackedIconButton as IconButton,
} from '../../../components/TrackedComponents';
import { useAlert } from '../../../hooks/alert';
import useApi from '../../../hooks/api';
import { CustomerConfigPageDefs } from '../../../utils/Pages';
import { DEFAULT_TITLE } from '../../Layout';

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

interface ExperimentDevicesComponentProps {
    experiment: ExperimentConfig;
    loadExperiments: () => void;
    onClose: () => void;
}

const ExperimentDevicesComponent: React.FC<ExperimentDevicesComponentProps> = (props) => {
    const [deviceIds, setDeviceIds] = useState<string[]>([]);
    const [action, setAction] = useState<ExperimentDeviceRequestActionEnum | ''>('');
    const [filter, setFilter] = useState<string>('');
    const [removeDeviceIds, setRemoveDeviceIds] = useState<Set<string>>(new Set());
    const [addDeviceIds, setAddDeviceIds] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [alertElement, setAlert] = useAlert();
    const { t } = useTranslation();
    const { controlApi } = useApi();

    useEffect(() => {
        document.title = `${DEFAULT_TITLE} | ${t(`navigator.${CustomerConfigPageDefs.name}`)}`;
    }, []);

    useEffect(() => {
        controlApi.controlV0ExperimentConfigExperimentIdGet({ experimentId: props.experiment.id }).then((res) => {
            setDeviceIds(res.data);
        });
    }, []);

    if (!props.experiment) {
        return null;
    }

    const updateDevices = (sample = false) => {
        if (action === '' && !sample) {
            return;
        }
        setLoading(true);
        const device_ids =
            action === 'add' ? addDeviceIds.split(',').map((d) => d.trim()) : Array.from(removeDeviceIds);
        controlApi
            .controlV0ExperimentConfigExperimentIdPost({
                experimentId: props.experiment.id,
                experimentDeviceRequest: {
                    action: action ? action : ExperimentDeviceRequestActionEnum.Sample,
                    device_ids,
                },
            })
            .then((res) => {
                setDeviceIds(res.data);
                setAction('');
                setAddDeviceIds('');
                setRemoveDeviceIds(new Set());
                props.loadExperiments();
            })
            .catch((res) => {
                setAlert({
                    message: res.response.data.message,
                    type: 'error',
                    duration: 6000,
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const filteredDeviceIds = deviceIds.filter((d) => d.includes(filter));

    return (
        <Dialog
            id="experiment-devices-dialog"
            open={true}
            onClose={() => props.onClose()}
            sx={{ p: 2 }}
            maxWidth="md"
            fullWidth
        >
            {alertElement}
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'secondary.main',
                    p: 0.5,
                }}
            >
                <Box>Experiment Devices</Box>
                <Box>
                    <IconButton
                        id="remove-devices-btn"
                        onClick={() => setAction(ExperimentDeviceRequestActionEnum.Remove)}
                        disabled={action != '' || deviceIds.length === 0}
                    >
                        <RemoveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        id="add-devices-btn"
                        onClick={() => setAction(ExperimentDeviceRequestActionEnum.Add)}
                        disabled={action != '' || deviceIds.length >= props.experiment.maximum_vehicles}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        id="sample-devices-btn"
                        onClick={() => updateDevices(true)}
                        disabled={action != '' || deviceIds.length >= props.experiment.maximum_vehicles}
                    >
                        <LocalDrinkIcon fontSize="small" />
                    </IconButton>
                    <IconButton id="close-devices-btn" onClick={() => props.onClose()}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
            <Box sx={{ p: 1, overflowY: 'auto' }}>
                <Grid container spacing={2}>
                    {filteredDeviceIds.map((deviceId) => (
                        <Grid item xs={4} key={deviceId}>
                            <Item
                                onClick={() => {
                                    if (action === 'remove') {
                                        const newDeviceIds = new Set(removeDeviceIds);
                                        if (newDeviceIds.has(deviceId)) {
                                            newDeviceIds.delete(deviceId);
                                        } else {
                                            newDeviceIds.add(deviceId);
                                        }
                                        setRemoveDeviceIds(new Set(newDeviceIds));
                                    }
                                }}
                                sx={{
                                    cursor: action === 'remove' ? 'pointer' : 'default',
                                    backgroundColor: removeDeviceIds.has(deviceId) ? 'secondary.main' : 'inherit',
                                }}
                            >
                                {deviceId}
                            </Item>
                        </Grid>
                    ))}
                </Grid>
            </Box>
            <Box sx={{ p: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    {action === 'add' ? (
                        <>
                            <TextField
                                id="device-ids"
                                label="Device IDs"
                                variant="outlined"
                                value={addDeviceIds}
                                onChange={(e) => setAddDeviceIds(e.target.value)}
                                sx={{ flexGrow: 1 }}
                                size="small"
                                disabled={loading}
                            />
                            <Button
                                id="cancel-add-devices-btn"
                                onClick={() => {
                                    setAction('');
                                    setAddDeviceIds('');
                                }}
                                variant="contained"
                                color="primary"
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                id="submit-new-devices-btn"
                                onClick={() => updateDevices()}
                                variant="contained"
                                color="secondary"
                                disabled={loading}
                                endIcon={loading ? <CircularProgress size="small" /> : null}
                            >
                                Add Devices
                            </Button>
                        </>
                    ) : (
                        <TextField
                            id="filter-device-ids"
                            label="Filter"
                            variant="standard"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            sx={{ flexGrow: 1 }}
                            InputProps={{ startAdornment: <SearchIcon fontSize="small" /> }}
                            size="small"
                        />
                    )}
                    {action === 'remove' ? (
                        <>
                            <Button
                                id="cancel-remove-devices-btn"
                                onClick={() => {
                                    setAction('');
                                    setRemoveDeviceIds(new Set());
                                }}
                                variant="contained"
                                color="primary"
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                id="select-all-btn"
                                onClick={() => setRemoveDeviceIds(new Set(filteredDeviceIds))}
                                variant="contained"
                                color="primary"
                                disabled={loading}
                            >
                                Select All
                            </Button>
                            <Button
                                id="remove-devices-btn"
                                onClick={() => updateDevices()}
                                variant="contained"
                                color="secondary"
                                disabled={loading}
                                endIcon={loading ? <CircularProgress size="small" /> : null}
                            >
                                Remove Devices
                            </Button>
                        </>
                    ) : null}
                </Box>
            </Box>
        </Dialog>
    );
};

export default ExperimentDevicesComponent;
